"use client";

import { useId } from "react";
import {
  format,
  isBefore,
  startOfDay,
  startOfMonth,
  startOfQuarter,
} from "date-fns";
import {
  getDefaultDateRangeFilter,
  GridDateRangeFilter,
  useGrid,
} from "@aleksundshantu/aunds-utils-react/primitives/grid";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";

import { Tooltip } from "@/components/Tooltip/Tooltip";
import styles from "./EventGrid.module.scss";

interface EventGridControlsProps {
  allowPastEvents: boolean;
  dateRangeType: "quarter" | "mothly";
  showPastEvents: boolean;
  onShowPastEventsChange: (showPastEvents: boolean) => void;
  showPastEventsToggle?: boolean;
}

const EventGridControls = ({
  allowPastEvents,
  dateRangeType,
  showPastEvents,
  onShowPastEventsChange,
  showPastEventsToggle = true,
}: EventGridControlsProps) => {
  const { getLabel } = useGlobalContext();
  const { filter, resetGrid, setFilter } = useGrid();
  const toggleLabelId = useId();
  const dateRangeFilter = filter.dateRangeFilter;
  const toggleLabel = getLabel(
    "show_past_events",
    "Vergangene Termine anzeigen",
  );

  // Compare day-granular: with the toggle off the grid clamps the filter's
  // startDate to `new Date()` (time of day), so raw comparisons would
  // misclassify the current period.
  const today = startOfDay(new Date());
  const isPastPeriod =
    !!dateRangeFilter && isBefore(startOfDay(dateRangeFilter.endDate), today);
  const isToggleDisabled = !allowPastEvents || isPastPeriod;

  const handleToggle = () => {
    if (!dateRangeFilter || isToggleDisabled) {
      return;
    }

    const nextShowPastEvents = !showPastEvents;
    const resetToCurrentDate =
      !nextShowPastEvents && dateRangeFilter.endDate < startOfDay(new Date())
        ? new Date()
        : undefined;
    const selectedDate = resetToCurrentDate ?? dateRangeFilter.date;

    if (resetToCurrentDate) {
      const queryDate =
        dateRangeType === "quarter"
          ? startOfQuarter(resetToCurrentDate)
          : startOfMonth(resetToCurrentDate);
      const queryParams = new URLSearchParams(window.location.search);
      queryParams.set("date", format(queryDate, "yyyyMM"));
      window.history.pushState(null, "", `?${queryParams.toString()}`);
    }

    onShowPastEventsChange(nextShowPastEvents);
    resetGrid();
    setFilter({
      ...filter,
      dateRangeFilter: getDefaultDateRangeFilter(
        {
          type: dateRangeType,
          showOldEvents: nextShowPastEvents,
        },
        format(selectedDate, "yyyyMM"),
      ),
    });
  };

  return (
    <div className={styles["controls"]}>
      <div className={`date-range-filter ${styles["date-range-filter"]}`}>
        <GridDateRangeFilter />
      </div>
      {showPastEventsToggle && (
        <Tooltip
          rules={[
            {
              condition: isPastPeriod,
              content: getLabel(
                "label_only_bygone_events_tooltip",
                "In diesem Zeitraum gibt es nur vergangene Termine",
              ),
            },
            {
              condition: !allowPastEvents,
              content: getLabel(
                "label_no_bygone_events_tooltip",
                "In diesem Zeitraum gibt es keine vergangenen Termine",
              ),
            },
          ]}
        >
          <div className={styles["past-events-toggle"]}>
            <span id={toggleLabelId} className={styles["toggle-label"]}>
              {toggleLabel}
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={showPastEvents}
              aria-disabled={isToggleDisabled}
              aria-labelledby={toggleLabelId}
              className={styles["toggle"]}
              onClick={handleToggle}
            >
              <span className="sr-only">{toggleLabel}</span>
            </button>
          </div>
        </Tooltip>
      )}
    </div>
  );
};

export default EventGridControls;
